import type { RouteLocationNormalizedGeneric, RouteRecordRaw } from "vue-router"
import type { RoleType } from "@/api/login/types/login"
import type { RouteAccessContext } from "@/utils/permissionRoutes"
import { defineStore } from "pinia"
import { markRaw, ref } from "vue"
import asyncRouteSettings from "@/config/async-route"
import { NoPermissionPageName } from "@/constants"
import StoreSectionLayout from "@/layout/components/NavigationBar/StoreSectionLayout.vue"
import { asyncRoutes } from "@/router"
import { constantRoutes } from "@/router/routes/index"
import store from "@/store"
import { getComponentName } from "@/utils"
import { hasAccess as checkRouteAccess, filterAuthorizedRoutes, filterNavigationRoutes, flattenRoutesWithFullPath, routePathMatches } from "@/utils/permissionRoutes"
import { getDefaultRouteName } from "@/utils/routeHelper"
import { organizeStoreRoutes } from "@/utils/storeNavigation"
import { findFirstAccessibleRoute } from "./permissionDefaultRoute"

const ErrorPage403 = () => import("@/views/base-page/error-page/ErrorPage403.vue")
const StoreDirectory = () => import("@/views/page/StoreDirectory.vue")
export { hasAccess } from "@/utils/permissionRoutes"

// 递归设置默认的 RouteName
const setDefaultRouteName = (routes: RouteRecordRaw[]) => {
  routes.forEach((route) => {
    if (!route.name) {
      route.name = getDefaultRouteName(route)
    }
    if (route.children) {
      setDefaultRouteName(route.children)
    }
  })
}

export const usePermissionStore = defineStore("permission", () => {
  const routes = ref<RouteRecordRaw[]>([])
  const dynamicRoutes = ref<RouteRecordRaw[]>([])
  const flattenedAllDynamicRoutes = ref<Array<RouteRecordRaw & { fullPath: string }>>([])
  const flattenedAccessibleDynamicRoutes = ref<Array<RouteRecordRaw & { fullPath: string }>>([])
  const flattenRoutes = (routes: RouteRecordRaw[]): RouteRecordRaw[] => {
    return routes.reduce((acc, route) => {
      acc.push(route)
      if (route.children) {
        acc.push(...flattenRoutes(route.children))
      }
      return acc
    }, [] as RouteRecordRaw[])
  }
  const flattenedDynamicRoutes = ref<RouteRecordRaw[]>([])

  const getParentRouteName = (route: RouteLocationNormalizedGeneric) => {
    // 获取父路由的名称
    return route.matched.length > 1 ? route.matched[route.matched.length - 2].name : undefined
  }

  const getNewRouter = (
    route: RouteLocationNormalizedGeneric,
    roles: RoleType[],
    permissionList: string[],
    context: RouteAccessContext = {},
  ) => {
    const matched = route.matched[route.matched.length - 1]
    const currentComponent = matched.components?.default
    const is403Page = getComponentName(currentComponent) === NoPermissionPageName
    const hadAccess = checkRouteAccess(matched, roles, permissionList, context)
    // 从dynamicRoutes中获取与matched匹配的路由
    const newRoute: RouteRecordRaw | undefined = flattenedDynamicRoutes.value.find((r) => r.name === matched.name)
    if (newRoute && matched.components?.default) {
      // 权限从无到有
      if (hadAccess && is403Page) {
        return newRoute
      }
      // 权限从有到无
      if (!hadAccess && !is403Page) {
        return {
          ...newRoute,
          component: ErrorPage403,
        } as RouteRecordRaw
      }
    }
  }

  const getFlattenedAccessibleRoutes = () => flattenRoutes(dynamicRoutes.value)

  const getFirstAccessibleRoute = () => findFirstAccessibleRoute(dynamicRoutes.value)

  const hasRouteName = (name: string | symbol) => {
    return getFlattenedAccessibleRoutes().some((route) => route.name === name)
  }

  const canAccessRoute = (route: RouteLocationNormalizedGeneric) => {
    const isKnownDynamicPath = flattenedAllDynamicRoutes.value.some(item => routePathMatches(item.fullPath, route.path))
    if (isKnownDynamicPath) {
      return flattenedAccessibleDynamicRoutes.value.some(item => routePathMatches(item.fullPath, route.path))
    }

    const matchedRoute = route.matched[route.matched.length - 1]
    if (!matchedRoute?.name) {
      return true
    }
    const isDynamicRoute = flattenedDynamicRoutes.value.some((item) => item.name === matchedRoute.name)
    return !isDynamicRoute || hasRouteName(matchedRoute.name)
  }

  const setRoutes = (roles: RoleType[], permissionList: string[], context: RouteAccessContext = {}) => {
    let accessedRoutes
    if (!asyncRouteSettings.open) {
      accessedRoutes = asyncRoutes
    } else {
      accessedRoutes = filterAuthorizedRoutes(asyncRoutes, roles, permissionList, "", context)
    }
    const originalNavigationRoutes = asyncRouteSettings.open
      ? filterNavigationRoutes(asyncRoutes, roles, permissionList, "", context)
      : accessedRoutes
    const project = (items: RouteRecordRaw[]) => organizeStoreRoutes(items, context, markRaw(StoreSectionLayout), StoreDirectory)
    const navigationRoutes = project(originalNavigationRoutes)
    accessedRoutes = project(accessedRoutes)
    routes.value = constantRoutes.concat(navigationRoutes).sort((a, b) => {
      const orderA = (a.meta?.order as number) ?? 0
      const orderB = (b.meta?.order as number) ?? 0
      return orderA - orderB
    })
    setDefaultRouteName(accessedRoutes)
    dynamicRoutes.value = accessedRoutes
    flattenedDynamicRoutes.value = flattenRoutes(asyncRoutes)
    flattenedAllDynamicRoutes.value = [...flattenRoutesWithFullPath(asyncRoutes), ...flattenRoutesWithFullPath(project(asyncRoutes))]
    flattenedAccessibleDynamicRoutes.value = flattenRoutesWithFullPath(accessedRoutes)
  }

  return { routes, dynamicRoutes, setRoutes, getNewRouter, getParentRouteName, getFirstAccessibleRoute, hasRouteName, canAccessRoute }
})

/** 在 setup 外使用 */
export function usePermissionStoreHook() {
  return usePermissionStore(store)
}
